import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

import { BrowserTabTitle } from '../../../components/display';
import { Alert, Button, FormValidation, EllipsisLoader } from '../../../components/custom-essentials';
import { FormikControl, checkResponse } from '../../../components/form';

import {
    login,
    googleLogin
} from '../../../actions';

const LoginSchema = Yup.object().shape({
    email: Yup.string()
        .email("Wrong email format")
        .min(3, "Minimum 3 symbols")
        .max(50, "Maximum 50 symbols")
        .required('Required Field'),
    password: Yup.string()
        .min(3, "Minimum 3 symbols")
        .max(50, "Maximum 50 symbols")
        .required('Required field'),
});

function Login(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: LoginSchema,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            await props.login(values.email, values.password)
                .then(async (response) => {
                    if (mounted.current) {
                        setSubmitting(false);
                        checkResponse(response, mounted, setStatus, { hideResponseCode: true, loginMuteOnSuccess: true });
                    }
                })
                .catch((response) => {
                    console.error(response);
                    if (mounted.current) {
                        setSubmitting(false);
                        setStatus(`Unexpected error (${response && response.status})`);
                    }
                })
        },
    });

    const hostname = window.location.hostname;
    const isMemberPortal = hostname.includes('member.');
    const loginTitle = isMemberPortal ? 'Member Login' : 'Admin Login';
    const loginSubtitle = isMemberPortal
        ? 'Enter your email and password to access MathPlex & ReadingPlex Member Portal'
        : 'Enter your email and password';

    // Google OAuth Client ID - Replace with actual credentials
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || ''; // To be configured

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const response = await props.googleLogin(credentialResponse.credential);
            if (mounted.current) {
                checkResponse(response, mounted, formik.setStatus, { hideResponseCode: true, loginMuteOnSuccess: true });
            }
        } catch (error) {
            console.error('Google login error:', error);
            if (mounted.current) {
                formik.setStatus('Google login failed. Please try again.');
            }
        }
    };

    const handleGoogleError = () => {
        if (mounted.current) {
            formik.setStatus('Google login failed. Please try again.');
        }
    };

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div className="px-4 sm:px-6 md:px-8 max-w-md mx-auto py-4">
                <BrowserTabTitle content={loginTitle} />
            {/* Headers */}
            <div className="text-lg sm:text-xl mb-1">
                {loginTitle}
            </div>
            <div className="text-xs sm:text-sm text-center mb-3 text-mpLGrey-3">
                {loginSubtitle}
            </div>
            <div className="mb-2">
                {formik.status ?
                    <Alert color="mpLRed" content={formik.status} />
                    :
                    <Alert color="mpLBlue" content={"Welcome back!"} />
                }
            </div>
            <div className="mb-3">
                {/*begin::Form*/}
                <form
                    onSubmit={formik.handleSubmit}
                >
                    <div className="space-y-3">
                        <div>
                            <FormikControl
                                id="login-email"
                                name="email"
                                placeholder="Email"
                                disabled={formik.isSubmitting}
                                value={formik.values.email}
                                onChange={formik.handleChange}
                            />
                            {formik.touched.email && formik.errors.email ? (
                                <FormValidation color="mpLRed" content={formik.errors.email} />
                            ) : null}
                        </div>
                        <div>
                            <FormikControl
                                id="login-password"
                                type="password"
                                name="password"
                                placeholder="Password"
                                disabled={formik.isSubmitting}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                shouldHandleSubmit
                                onSubmit={formik.handleSubmit}
                            />
                            {formik.touched.password && formik.errors.password ? (
                                <FormValidation color="mpLRed" content={formik.errors.password} />
                            ) : null}
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-0">
                        <Button
                            color="lte-mpTeal"
                            onClick={() => null}
                            className="w-full sm:w-auto sm:mr-4"
                        >
                            <Link
                                disabled={formik.isSubmitting}
                                to="/auth/forgot-password"
                                className="block w-full text-center"
                            >
                                Forgot Password
                            </Link>
                        </Button>
                        <Button
                            color="lte-mpLBlue"
                            disabled={formik.isSubmitting}
                            onClick={formik.handleSubmit}
                            className="w-full sm:w-auto"
                        >
                            Sign In
                        </Button>{formik.isSubmitting && <EllipsisLoader />}
                    </div>
                </form>

                {/* Google OAuth Login - Member Portal Only */}
                {isMemberPortal && (
                    <>
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or continue with</span>
                            </div>
                        </div>
                        
                        {googleClientId ? (
                            <>
                                <div className="flex justify-center">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                        useOneTap
                                        text="signin_with"
                                        shape="rectangular"
                                        size="large"
                                        width="300"
                                    />
                                </div>
                                <div className="mt-4 text-xs text-center text-gray-500">
                                    Sign in with your Google account if your email matches your member account
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-center">
                                <div className="bg-gray-100 border border-gray-300 rounded-lg px-6 py-3 text-center">
                                    <div className="text-gray-600 text-sm mb-1">
                                        🔐 Google Sign-In Available
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Contact admin to enable Google authentication
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            </div>
        </GoogleOAuthProvider>
    );
}

export default connect(null, {
    login,
    googleLogin
})(Login);